import React, { useCallback, useEffect, useReducer, useState } from "react"
import {
    obterArquivos,
    uploadArquivo as uploadArquivoServico,
    removerArquivo as removerArquivoServico,
} from "../../../services/arquivosAplicativo"
import useLoadingStore from "../../../stores/LoadingStore"
import { Loading } from "../../RegistradorDePedidos"
import { ArquivosAplicativoInicial } from "../models/arquivosAplicativoModel"
import { ArquivosAplicativoActionsTypes } from "./arquivosAplicativoActions"
import { ArquivosAplicativoContext } from "./ArquivosAplicativoContext"
import { arquivosAplicativoReducer } from "./arquivosAplicativoReducer"
import type { ArquivosAplicativoModel } from "../models/arquivosAplicativoModel"

export function ArquivosAplicativoContextProvider({ children }: { children: React.ReactNode }) {
    const [arquivos, dispatch] = useReducer(arquivosAplicativoReducer, ArquivosAplicativoInicial)
    const [erroCarregamento, setErroCarregamento] = useState<string | null>(null)
    const setLoading = useLoadingStore(s => s.setLoading)

    const carregar = async () => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        try {
            setLoading(true)
            const response = await obterArquivos(autorizacao)
            if ("retornoErro" in response) {
                setErroCarregamento(response.mensagem || "Erro ao carregar os arquivos do aplicativo")
            } else {
                setErroCarregamento(null)
                dispatch({ type: ArquivosAplicativoActionsTypes.CARREGAR, payload: response })
            }
        } catch {
            setErroCarregamento("Erro ao carregar os arquivos do aplicativo")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        carregar()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const uploadArquivo = useCallback(async (campo: string, file: File) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const response = await uploadArquivoServico(autorizacao, campo, file)
            if ("retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({
                type: ArquivosAplicativoActionsTypes.ATUALIZAR_URL,
                payload: {
                    campo: campo as keyof Omit<ArquivosAplicativoModel, "podeEditar" | "ehClienteFiel">,
                    url: response.url,
                },
            })
        } finally {
            setLoading(false)
        }
    }, [setLoading])

    const removerArquivo = useCallback(async (campo: string) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setLoading(true)
        try {
            const response = await removerArquivoServico(autorizacao, campo)
            if (response && "retornoErro" in response) {
                throw new Error(response.mensagem)
            }
            dispatch({
                type: ArquivosAplicativoActionsTypes.ATUALIZAR_URL,
                payload: {
                    campo: campo as keyof Omit<ArquivosAplicativoModel, "podeEditar" | "ehClienteFiel">,
                    url: null,
                },
            })
        } finally {
            setLoading(false)
        }
    }, [setLoading])

    return (
        <ArquivosAplicativoContext.Provider
            value={{ arquivos, dispatch, erroCarregamento, uploadArquivo, removerArquivo }}
        >
            <Loading />
            {children}
        </ArquivosAplicativoContext.Provider>
    )
}
